import { Outlet, Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import Sidebar from './Sidebar';
import { Loader2, AlertTriangle, X, Menu, LogOut } from 'lucide-react';
import { useState, useMemo } from 'react';

function TrialBanner({ company }) {
  const { t } = useLanguage();
  const [dismissed, setDismissed] = useState(false);
  
  const trialInfo = useMemo(() => {
    if (!company || company.subscription_status !== 'trial') return null;
    if (!company.trial_ends_at) return null;
    const trialEnd = new Date(company.trial_ends_at);
    const now = new Date();
    const daysLeft = Math.ceil((trialEnd - now) / (1000 * 60 * 60 * 24));
    return { daysLeft, expired: daysLeft <= 0 };
  }, [company]);
  
  if (!trialInfo || dismissed) return null;
  if (trialInfo.daysLeft > 7) return null;
  
  const isUrgent = trialInfo.daysLeft <= 3;
  
  return (
    <div className={`relative flex items-center gap-3 px-4 py-2.5 text-sm font-medium ${
      isUrgent 
        ? 'bg-rose-500/20 border-b border-rose-500/30 text-rose-300' 
        : 'bg-amber-500/20 border-b border-amber-500/30 text-amber-300'
    }`}>
      <AlertTriangle className="h-4 w-4 shrink-0" />
      <span>
        {trialInfo.daysLeft <= 0
          ? t('trial.expired')
          : t('trial.daysLeft').replace('{n}', trialInfo.daysLeft)
        }
        {' '}
        <a href="mailto:support@workplus.ee" className="underline hover:no-underline">
          {t('trial.contactSupport')}
        </a>
      </span>
      <button
        onClick={() => setDismissed(true)}
        className="ml-auto p-0.5 opacity-60 hover:opacity-100 transition-opacity"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

export default function Layout() {
  const { isAuthenticated, loading, company, isAdmin, isHR, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#020617]">
        <div className="relative">
          <Loader2 className="h-10 w-10 animate-spin text-indigo-500" />
          <div className="absolute inset-0 blur-xl bg-wp-medium/30 animate-pulse" />
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="flex min-h-screen h-dvh overflow-hidden bg-[#020617] mesh-gradient" data-testid="app-layout">
      <div className="noise-overlay" />

      {/* Mobile overlay backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/60 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar — hidden on mobile, shown as overlay when open */}
      <div className={`
        fixed md:static inset-y-0 left-0 z-50
        transform transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
      `}>
        <Sidebar onClose={() => setSidebarOpen(false)} />
      </div>

      {/* Main content */}
      <main className="flex-1 overflow-y-auto relative flex flex-col min-w-0">
        {/* Mobile top header */}
        <div className="flex md:hidden items-center gap-3 px-4 py-3 border-b border-wp-dark/30 bg-wp-darker sticky top-0 z-30" style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top, 0px))' }}>
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 rounded-xl text-wp-light/70 hover:text-wp-lightest hover:bg-wp-dark/40 transition-colors"
          >
            <Menu className="h-5 w-5" />
          </button>
          <span className="text-lg font-light tracking-wide text-white font-heading">
            WORK<span className="text-wp-light">+</span>
          </span>
          <button
            onClick={logout}
            className="ml-auto p-2 rounded-xl text-wp-light/40 hover:text-rose-400 hover:bg-rose-500/10 transition-colors"
            title="Logout"
          >
            <LogOut className="h-5 w-5" />
          </button>
        </div>

        {(isAdmin || isHR) && <TrialBanner company={company} />}

        <div className="p-4 md:p-8 max-w-[1600px] mx-auto page-enter flex-1 w-full" style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom, 0px) + 56px)' }}>
          <Outlet />
        </div>
      </main>
    </div>
  );
}
